import React, { useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Chip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import cls from "./Status.module.css";
import {
  TIMELINE_HOURS_LIST,
  getTimelineNowBound,
  clampTimelineSelection,
} from "../../lib/statusBoardDiff";

const HOURS = TIMELINE_HOURS_LIST;
const MINUTE_TICKS = [0, 10, 20, 30, 40, 50];

function minuteLeftPct(m, max) {
  if (max <= 0) return 0;
  return (m / max) * 100;
}

const trackSliderSx = {
  color: "#ffca28",
  height: 6,
  py: 0.5,
  "& .MuiSlider-rail": {
    opacity: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: "rgba(255,255,255,0.12)",
  },
  "& .MuiSlider-track": {
    height: 6,
    borderRadius: 3,
    background: "linear-gradient(90deg, #ff8f00 0%, #ffca28 100%)",
    border: "none",
  },
  "& .MuiSlider-thumb": {
    width: 14,
    height: 14,
    backgroundColor: "#fff",
    border: "2px solid #ffb300",
    boxShadow: "0 1px 6px rgba(0,0,0,0.35)",
  },
  "& .MuiSlider-mark": { display: "none" },
  "& .MuiSlider-markLabel": { display: "none" },
};

const minuteSliderSx = {
  ...trackSliderSx,
  height: 5,
  py: 0.35,
  "& .MuiSlider-rail": { height: 5 },
  "& .MuiSlider-track": {
    height: 5,
    background: "linear-gradient(90deg, #42a5f5 0%, #90caf9 100%)",
  },
  "& .MuiSlider-thumb": {
    width: 12,
    height: 12,
    borderColor: "#42a5f5",
  },
};

function eventLeftPct(index, max) {
  if (max <= 0) return 0;
  return (index / max) * 100;
}

function RowTransport({
  disabled,
  isPlaying,
  playDisabled,
  prevDisabled,
  nextDisabled,
  onPrev,
  onPlayToggle,
  onNext,
  timeLabel,
}) {
  return (
    <Box className={cls.timelineRowSide}>
      <Box className={cls.timelineTransport}>
        <IconButton
          size="small"
          disabled={disabled || prevDisabled}
          onClick={onPrev}
          className={cls.timelineIconBtn}
          aria-label="이전"
        >
          <SkipPreviousIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          disabled={disabled || playDisabled}
          onClick={onPlayToggle}
          className={`${cls.timelineIconBtn} ${isPlaying ? cls.timelineIconBtnActive : ""}`}
          aria-label={isPlaying ? "일시정지" : "재생"}
        >
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: 16 }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
        <IconButton
          size="small"
          disabled={disabled || nextDisabled}
          onClick={onNext}
          className={cls.timelineIconBtn}
          aria-label="다음"
        >
          <SkipNextIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <Chip label={timeLabel} size="small" className={cls.timelineTimeChip} />
    </Box>
  );
}

export default function StatusTimelineBar({
  selectedHour,
  onHourChange,
  selectedMinute = 0,
  onMinuteChange,
  isPlayingHour,
  onPlayingHourChange,
  isPlayingMinute,
  onPlayingMinuteChange,
  changedHours = [],
  changedMinutes = [],
  isToday,
  disabled,
  onStepHourNext,
  onStepMinuteNext,
}) {
  const hourPlayTimerRef = useRef(null);
  const minutePlayTimerRef = useRef(null);
  const nowBound = getTimelineNowBound(isToday, HOURS);
  const tickScaleMax = HOURS.length - 1;

  const hourIdx = HOURS.indexOf(selectedHour ?? 9);
  const cappedHourIdx =
    hourIdx < 0
      ? 0
      : isToday
        ? Math.min(hourIdx, nowBound.maxHourIdx)
        : hourIdx;

  const minuteMax =
    isToday && selectedHour === nowBound.hour
      ? nowBound.maxMinute
      : 59;

  const atHourNowBound =
    isToday &&
    selectedHour === nowBound.hour &&
    (selectedMinute ?? 0) >= nowBound.maxMinute;

  const atMinuteNowBound =
    isToday &&
    selectedHour === nowBound.hour &&
    (selectedMinute ?? 0) >= nowBound.maxMinute;

  const endMarkerIdx =
    isToday && nowBound.endMarkerHour != null
      ? HOURS.indexOf(nowBound.endMarkerHour)
      : -1;
  const endMarkerPct =
    endMarkerIdx >= 0 ? eventLeftPct(endMarkerIdx, tickScaleMax) : null;
  const futureZoneLeft =
    endMarkerPct != null
      ? endMarkerPct
      : eventLeftPct(nowBound.maxHourIdx, tickScaleMax);

  useEffect(() => {
    if (!isPlayingHour) {
      if (hourPlayTimerRef.current) {
        clearInterval(hourPlayTimerRef.current);
        hourPlayTimerRef.current = null;
      }
      return undefined;
    }
    hourPlayTimerRef.current = setInterval(() => {
      onStepHourNext?.();
    }, 1200);
    return () => {
      if (hourPlayTimerRef.current) clearInterval(hourPlayTimerRef.current);
    };
  }, [isPlayingHour, onStepHourNext]);

  useEffect(() => {
    if (!isPlayingMinute) {
      if (minutePlayTimerRef.current) {
        clearInterval(minutePlayTimerRef.current);
        minutePlayTimerRef.current = null;
      }
      return undefined;
    }
    minutePlayTimerRef.current = setInterval(() => {
      onStepMinuteNext?.();
    }, 800);
    return () => {
      if (minutePlayTimerRef.current) clearInterval(minutePlayTimerRef.current);
    };
  }, [isPlayingMinute, onStepMinuteNext]);

  const applyHourMinute = (hour, minute) => {
    const clamped = clampTimelineSelection(hour, minute, isToday, HOURS);
    onHourChange(clamped.hour);
    onMinuteChange?.(clamped.minute);
  };

  const handleHourSliderChange = (_, value) => {
    if (disabled) return;
    const clampedIdx = Math.max(
      0,
      Math.min(value, isToday ? nowBound.maxHourIdx : tickScaleMax),
    );
    applyHourMinute(HOURS[clampedIdx], 0);
  };

  const handleMinuteSliderChange = (_, value) => {
    if (disabled) return;
    const clamped = clampTimelineSelection(
      selectedHour,
      value,
      isToday,
      HOURS,
    );
    onMinuteChange?.(clamped.minute);
  };

  const stepHour = (delta) => {
    const idx = HOURS.indexOf(selectedHour ?? 9);
    const nextIdx = Math.max(
      0,
      Math.min(isToday ? nowBound.maxHourIdx : tickScaleMax, idx + delta),
    );
    applyHourMinute(
      HOURS[nextIdx],
      nextIdx === nowBound.maxHourIdx && isToday ? nowBound.maxMinute : 0,
    );
  };

  const stepMinute = (delta) => {
    const cur = selectedMinute ?? 0;
    const next = Math.max(0, Math.min(minuteMax, cur + delta));
    const clamped = clampTimelineSelection(
      selectedHour,
      next,
      isToday,
      HOURS,
    );
    onMinuteChange?.(clamped.minute);
  };

  const hourProgressPct = eventLeftPct(cappedHourIdx, tickScaleMax);
  const minuteProgressPct = minuteLeftPct(
    Math.min(selectedMinute ?? 0, minuteMax),
    minuteMax,
  );
  const visibleMinuteTicks = MINUTE_TICKS.filter((m) => m <= minuteMax);
  const activeMinuteTick = Math.floor((selectedMinute ?? 0) / 10) * 10;

  const hourTimeLabel = `${String(selectedHour ?? 9).padStart(2, "0")}:00`;
  const minuteTimeLabel =
    changedMinutes.length > 0
      ? `${String(selectedMinute ?? 0).padStart(2, "0")}분·${changedMinutes.length}`
      : `${String(selectedMinute ?? 0).padStart(2, "0")}분`;

  const toggleHourPlay = () => {
    const next = !isPlayingHour;
    onPlayingHourChange(next);
    if (next) onPlayingMinuteChange(false);
  };

  const toggleMinutePlay = () => {
    const next = !isPlayingMinute;
    onPlayingMinuteChange(next);
    if (next) onPlayingHourChange(false);
  };

  return (
    <Box className={cls.timelineBar}>
      <Box className={cls.timelineTrackPanel}>
        <Box className={cls.timelineCompactRow}>
          <RowTransport
            disabled={disabled}
            isPlaying={isPlayingHour}
            playDisabled={false}
            prevDisabled={cappedHourIdx <= 0}
            nextDisabled={
              isToday
                ? cappedHourIdx >= nowBound.maxHourIdx || atHourNowBound
                : cappedHourIdx >= tickScaleMax
            }
            onPrev={() => stepHour(-1)}
            onPlayToggle={toggleHourPlay}
            onNext={() => stepHour(1)}
            timeLabel={hourTimeLabel}
          />
          <Typography component="span" className={cls.timelineRowTitle}>
            H
          </Typography>
          <Box className={cls.timelineRowTrack}>
            <Box className={cls.timelineTrackShell}>
              {isToday && (
                <Box
                  className={cls.timelineFutureZone}
                  sx={{ left: `${futureZoneLeft}%` }}
                  aria-hidden
                />
              )}
              <Box
                className={cls.timelineTrackFill}
                sx={{
                  width: `${hourProgressPct}%`,
                  background:
                    "linear-gradient(90deg, rgba(255,143,0,0.35), rgba(255,202,40,0.55))",
                }}
              />
              {isToday && endMarkerPct != null && (
                <Box
                  className={cls.timelineEndMarker}
                  sx={{ left: `${endMarkerPct}%` }}
                  title={`${nowBound.endMarkerHour}시 이후 진행 금지`}
                >
                  <span className={cls.timelineEndMarkerLine} />
                  <span className={cls.timelineEndMarkerLabel}>종료</span>
                </Box>
              )}
              <Box className={cls.timelineEventLayer}>
                {HOURS.map((h, i) =>
                  changedHours.includes(h) &&
                  (!isToday || i <= nowBound.maxHourIdx) ? (
                    <span
                      key={`h-ev-${h}`}
                      className={cls.timelineEventDotHour}
                      style={{ left: `${eventLeftPct(i, tickScaleMax)}%` }}
                      title={`${h}시 변화`}
                    />
                  ) : null,
                )}
              </Box>
              <Slider
                size="small"
                min={0}
                max={tickScaleMax}
                step={1}
                value={Math.max(0, cappedHourIdx)}
                onChange={handleHourSliderChange}
                disabled={disabled}
                className={cls.timelineSliderOverlay}
                sx={{
                  ...trackSliderSx,
                  ...(isToday
                    ? {
                        "& .MuiSlider-rail": {
                          height: 6,
                          borderRadius: 3,
                          background: `linear-gradient(90deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.12) ${futureZoneLeft}%, rgba(255,82,82,0.15) ${futureZoneLeft}%, rgba(255,82,82,0.22) 100%)`,
                        },
                      }
                    : {}),
                }}
              />
            </Box>
            <Box className={cls.timelineHourTicks}>
              {HOURS.map((h, i) => {
                const isFuture = isToday && i > nowBound.maxHourIdx;
                const tickClass = [
                  cls.timelineHourTick,
                  selectedHour === h ? cls.timelineHourTickActive : "",
                  isFuture ? cls.timelineHourTickFuture : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <span
                    key={h}
                    className={tickClass}
                    style={{ left: `${eventLeftPct(i, tickScaleMax)}%` }}
                  >
                    {h}
                  </span>
                );
              })}
              {isToday &&
                nowBound.endMarkerHour != null &&
                endMarkerPct != null && (
                  <span
                    className={`${cls.timelineHourTick} ${cls.timelineHourTickEnd}`}
                    style={{ left: `${endMarkerPct}%` }}
                  >
                    {nowBound.endMarkerHour}
                  </span>
                )}
            </Box>
          </Box>
        </Box>

        <Box className={cls.timelineCompactRow}>
          <RowTransport
            disabled={disabled}
            isPlaying={isPlayingMinute}
            playDisabled={false}
            prevDisabled={(selectedMinute ?? 0) <= 0}
            nextDisabled={atMinuteNowBound}
            onPrev={() => stepMinute(-1)}
            onPlayToggle={toggleMinutePlay}
            onNext={() => stepMinute(1)}
            timeLabel={minuteTimeLabel}
          />
          <Typography component="span" className={cls.timelineRowTitle}>
            M
          </Typography>
          <Box className={cls.timelineRowTrack}>
            <Box className={cls.timelineTrackShell}>
              <Box className={cls.timelineMinuteTickLayer} aria-hidden>
                {visibleMinuteTicks.map((m) => (
                  <span
                    key={`m-tick-${m}`}
                    className={cls.timelineMinuteTickMark}
                    style={{ left: `${minuteLeftPct(m, minuteMax)}%` }}
                  />
                ))}
              </Box>
              <Box
                className={cls.timelineTrackFill}
                sx={{
                  width: `${minuteProgressPct}%`,
                  background:
                    "linear-gradient(90deg, rgba(66,165,245,0.3), rgba(144,202,249,0.5))",
                }}
              />
              <Box className={cls.timelineEventLayer}>
                {changedMinutes.map((m) => (
                  <span
                    key={`m-ev-${m}`}
                    className={cls.timelineEventDotMinute}
                    style={{ left: `${eventLeftPct(m, 59)}%` }}
                    title={`${m}분 변화`}
                  />
                ))}
              </Box>
              {isToday && selectedHour === nowBound.hour && (
                <Box
                  className={cls.timelineMinuteEndHint}
                  sx={{
                    left: `${eventLeftPct(nowBound.maxMinute, Math.max(minuteMax, 1))}%`,
                  }}
                >
                  현재
                </Box>
              )}
              <Slider
                size="small"
                min={0}
                max={minuteMax}
                step={1}
                value={Math.min(selectedMinute ?? 0, minuteMax)}
                onChange={handleMinuteSliderChange}
                disabled={disabled}
                className={cls.timelineSliderOverlay}
                sx={minuteSliderSx}
              />
            </Box>
            <Box className={cls.timelineMinuteTicks}>
              {visibleMinuteTicks.map((m) => {
                const tickClass = [
                  cls.timelineMinuteTick,
                  activeMinuteTick === m ? cls.timelineMinuteTickActive : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <span
                    key={`m-lbl-${m}`}
                    className={tickClass}
                    style={{ left: `${minuteLeftPct(m, minuteMax)}%` }}
                  >
                    {m}
                  </span>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className={cls.timelineLegend}>
        <Chip
          size="small"
          icon={<FiberManualRecordIcon sx={{ fontSize: "9px !important" }} />}
          label="변화"
          className={cls.legendChipChanged}
        />
        <Chip size="small" label="▲" className={cls.legendChipUp} />
        <Chip size="small" label="▼" className={cls.legendChipDown} />
      </Box>
    </Box>
  );
}
